/**
 * @Page WST01
 * @Description 실사재고 > 현황관리 > 실사재고등록
 * @Table WRTWQTY 실재고 수량
 **/
import React, { useState, useEffect, useRef, useCallback } from "react";
import Ribbon from '@/components/organisms/Ribbon';
import GridRibbon from "@/components/organisms/GridRibbon";
import ContentXearch from '@/components/organisms/ContentXearch';
import Content from '@/components/organisms/Content';
import Grid from '@/components/grid/agGrid';
import CustomAlert from "@/components/molecules/CustomAlert";
import AlertDialog from '@/components/material-UI/AlertDialog';
import * as Common from '@/utils/Common';
import CommonUtil from '@/utils/CommonUtil';

const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;
const Utils = new CommonUtil();

function WST01() {
    const token = Common.getToken();
    const userId = Common.getToken("id");
    const tVnt1 = Common.getToken("vand");
    const tComp = Common.getToken("comp");
    const ckVnt1 = Common.getCookie("ck_vnt1" + userId) ? Common.getCookie("ck_vnt1" + userId) : tVnt1;

    const grdWST01 = useRef();

    // ─── 상태 ────────────────────────────────────────────────────────────
    const [idata, setData] = useState({
        kVnt1: ckVnt1,
        kComp: tComp,
        kWrcd: "",              // 창고코드
        kStdt: Common.getToday(), // 일자
        kOrdr: "",              // 차수
        kGrtp: "",              // 품번그룹
        kPtno: "",              // 품번

        // 콤보 목록
        wrcdList: [],           // 창고코드 목록
        grptList: [],           // 품번그룹 목록
        ordrList: [],           // 차수 목록
    });

    const [message, setMessage] = useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;

    const [dlgMessage, setDlgMessage] = useState({ openDlg: false, dlgTrans: "", dlgMsg: "" });
    const { openDlg, dlgTrans, dlgMsg } = dlgMessage;

    let [searchApi, setSearchApi] = useState(false);
    let [insertApi, setInsertApi] = useState(false);
    let [deleteApi, setDeleteApi] = useState(false);

    const [panded, setPanded] = useState(true);
    const [style, setStyle] = useState({ height: "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)" });

    const onClearApi = useCallback(async () => {
        setSearchApi(false);
        setInsertApi(false);
        setDeleteApi(false);
    }, []);

    // ─── Mount ───────────────────────────────────────────────────────────
    useEffect(() => {
        onSearchWrcdList();
        onSearchGrptList();
    }, []);

    // 창고코드 목록 조회
    const onSearchWrcdList = async () => {
        const para = `{"kVnt1":"${ckVnt1}","kWrcd":"","wareyn":"*"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst01/ware/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const list = [{ comCode: "", text: "전체" },
                        ...data.data.map(w => ({ comCode: w.wrcd, text: `${w.wrcd} - ${w.wrnm}` }))];
                    setData(prev => ({ ...prev, wrcdList: list }));
                }
            })
            .catch(() => {});
    };

    // 품번그룹 목록 조회
    const onSearchGrptList = async () => {
        const para = `{"kVnt1":"${ckVnt1}","kGrtp":"","kGryn":"*"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/rst03/wagr/search?query=" + param;
        await fetch(query, { headers: { Authorization: token } })
            .then(resp => resp.json())
            .then(data => {
                if (data.success && data.data) {
                    const list = [{ comCode: "", text: "전체" },
                        ...data.data.map(g => ({ comCode: g.grtp, text: `${g.grtp} - ${g.grnm}` }))];
                    setData(prev => ({ ...prev, grptList: list }));
                }
            })
            .catch(() => {});
    };

    // ─── Control 변경 ────────────────────────────────────────────────────
    const onChange = async (e) => {
        const { name, value } = e.target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    // ─── 조회 ─────────────────────────────────────────────────────────────
    const onSearch = async () => {
        const stdt = idata.kStdt ? idata.kStdt.replace(/-/g, "") : "";
        const para = `{"kVnt1":"${idata.kVnt1}","kComp":"${idata.kComp}","wrcd":"${idata.kWrcd}","stdt":"${stdt}","ordr":"${idata.kOrdr}","grpt":"${idata.kGrtp}","ptno":"${idata.kPtno}"}`;
        const param = encodeURI(para);
        const query = SERVER_URL + "/wst01/qty/search?query=" + param;
        setSearchApi(query);
    };

    // ─── 신규 Row 추가 ────────────────────────────────────────────────────
    const onAddRow = async () => {
        const stdt = idata.kStdt ? idata.kStdt.replace(/-/g, "") : Common.getToday().replace(/-/g, "");
        const newItems = {
            vand: idata.kVnt1,
            wrcd: idata.kWrcd,
            stdt: stdt,
            ordr: idata.kOrdr,
            grtp: idata.kGrtp,
            ptno: idata.kPtno,
            waqt: "",
            etc:  "",
            gocd: "ADD",
        };
        grdWST01.current.addRow(newItems, 0);
    };

    // ─── 저장 (Insert/Update) ─────────────────────────────────────────────
    const onInsert = async () => {
        const selectedData = grdWST01.current.getSelectedNodes();
        if (selectedData.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "저장할 데이터를 선택해 주세요." });
            return;
        }
        for (const node of selectedData) {
            if (!node.data.wrcd) {
                setMessage({ openAlert: true, severity: "warning", msg: "창고코드를 입력해 주세요." });
                return;
            }
            if (!node.data.stdt) {
                setMessage({ openAlert: true, severity: "warning", msg: "일자를 입력해 주세요." });
                return;
            }
            if (!node.data.ptno) {
                setMessage({ openAlert: true, severity: "warning", msg: "품번을 입력해 주세요." });
                return;
            }
            if (node.data.waqt === "" || node.data.waqt === null || node.data.waqt === undefined) {
                setMessage({ openAlert: true, severity: "warning", msg: "수량을 입력해 주세요." });
                return;
            }
        }
        setInsertApi(SERVER_URL + "/wst01/qty/insert");
    };

    // ─── 삭제 ─────────────────────────────────────────────────────────────
    const onDelete = async () => {
        const selectedData = grdWST01.current.getSelectedNodes();
        if (selectedData.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "삭제할 데이터를 선택해 주세요." });
            return;
        }
        setDlgMessage({ openDlg: true, dlgTrans: "D", dlgMsg: "선택한 실사재고 데이터를 삭제하시겠습니까?" });
    };

    // ─── AlertDialog 결과 ────────────────────────────────────────────────
    const AlertDialogResult = async (e) => {
        setDlgMessage({ openDlg: false, dlgTrans: "", dlgMsg: "" });
        if (e.result === "N") return;
        switch (e.Trans) {
            case "D":
                setDeleteApi(SERVER_URL + "/wst01/qty/delete");
                break;
            default:
                break;
        }
    };

    // ─── 검색조건 ────────────────────────────────────────────────────────
    const optionsX = {
        0: [
            {
                formatter: "select", name: "kWrcd", labelName: "창고코드", type: "text",
                defaultValue: idata.kWrcd, codeMode: false, selectAll: "",
                codes: idata.wrcdList, comms: null,
            },
            {
                formatter: "input", name: "kStdt", labelName: "일자", type: "date",
                defaultValue: idata.kStdt, align: "left", labelType: "none",
            },
            {
                formatter: "input", name: "kOrdr", labelName: "차수", type: "text",
                defaultValue: idata.kOrdr, align: "left", labelType: "none",
            },
            {
                formatter: "select", name: "kGrtp", labelName: "품번그룹", type: "text",
                defaultValue: idata.kGrtp, codeMode: false, selectAll: "",
                codes: idata.grptList, comms: null,
            },
            {
                formatter: "input", name: "kPtno", labelName: "품번", type: "text",
                defaultValue: idata.kPtno, readOnly: false, align: "left", labelType: "none",
            },
            { formatter: "blank", align: "right" },
            { formatter: "blank", align: "right" },
        ],
    };

    // ─── Grid 컬럼 (WRTWQTY 기준) ─────────────────────────────────────────
    const columnDefs = [
        { headerName: "업체코드(h)", field: "vand", hide: true },
        {
            headerName: "창고코드", field: "wrcd",
            sortable: true, filter: true, flex: 1, minWidth: 90,
            editable: false, cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true, hide: false,
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            //headerName: "일시", field: "stdtexample",
            headerName: "일시", field: "stdt",
            sortable: true, filter: true, flex: 1, minWidth: 100,
            editable: false,  cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true, hide: false,
        },
         {
            headerName: "구분용", field: "stdtgubn2",
            sortable: true, filter: true, flex: 1, minWidth: 100,
            editable: false,  cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true, hide: false,
        }, 
        {
            headerName: "차수", field: "ordrdisp",
            sortable: true, filter: true, flex: 1, minWidth: 60,
            editable: false,  cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true, hide: false,
        },
        {
            headerName: "품번그룹", field: "grpt",
            sortable: true, filter: true, flex: 1, minWidth: 100,
            editable: false,  cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true, hide: false,
        },
        {
            headerName: "품번", field: "ptno",
            sortable: true, filter: true, flex: 2, minWidth: 150,
            editable: false,  cellClass: ["nocheckbox", "alignL"],
            singleClickEdit: true, hide: false,
            valueGetter: Utils.partnoGetter,
        },
        {
            headerName: "수량", field: "waqt",
            sortable: true, filter: true, flex: 1, minWidth: 80,
            editable: true, headerClass: "editCell", cellClass: ["nocheckbox", "alignR"],
            singleClickEdit: true, hide: false,
            type: "numericColumn"
            //, valueFormatter: Utils.numberFormatter
            , cellStyle: params => {
                if (params.data.stdtgubn2 === "Y") {
                        return { color: "rgb(78 92 217)", fontWeight: "bold" };
                } return { color: "#6e6e6e", fontWeight: "bold" };
            }
        },
        {
            headerName: "상주원", field: "renm",
            sortable: true, filter: true, flex: 1, minWidth: 90,
            editable: false, headerClass: "", cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: false, hide: false,
        },
        {
            headerName: "비고(수량비고)", field: "etc",
            sortable: true, filter: true, flex: 2, minWidth: 150,
            editable: true, headerClass: "editCell", cellClass: ["nocheckbox", "alignL"],
            singleClickEdit: true, hide: false,
        },
    ];

    const onClick = async () => {
        setPanded(!panded);
        if (panded === true) {
            setStyle({ height: "calc(100vh - 65px - 35px - 50px - 21px - 8px)" });
        } else {
            setStyle({ height: "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)" });
        }
    };

    return (
        <>
            {/* Dialog */}
            <AlertDialog open={openDlg} trans={dlgTrans} message={dlgMsg} onClose={AlertDialogResult} />

            {/* Alert */}
            <CustomAlert
                open={openAlert} severity={severity} duration={5000} message={msg}
                onClose={() => setMessage({ openAlert: false, severity: "", msg: "" })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            />

            {/* Ribbon */}
            <Ribbon
                onSearch={onSearch} onSearchName={null}
                onInsert={onInsert} onInsertName={"저장"}
                onDelete={onDelete} onDeleteName={"삭제"}
            >
                {"실사재고 > 현황관리 > "}
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardMedium, NanumGothic, sans-serif" }}>
                    실사재고등록
                </b>
            </Ribbon>

            {/* 검색조건 */}
            <ContentXearch optionsX={optionsX} onChange={onChange} onClick={onClick} />

            {/* Content */}
            <Content style={style}>
                <div className="grid_type01">
                    <div className="grid_wrap01">
                        <GridRibbon
                            title={"실사재고 수량 (WRTWQTY)"}
                            onSearch={onSearch}   onSearchName={null}
                            onAddRow={onAddRow}   onAddRowName={"신규"}
                            onInsert={onInsert}   onInsertName={"저장"}
                            onDelete={onDelete}   onDeleteName={"삭제"}
                        />
                        <div className="grid_cont_box" style={{ height: "calc(100%)" }}>
                            <Grid
                                ref={grdWST01}
                                gridId={"WST01"}
                                columnDefs={columnDefs}
                                pagination={true}
                                paginationCsv={true}
                                paginationPageSize={"1000"}
                                rowMultiSelectWithClick={true}
                                suppressRowClickSelection={false}
                                rowSelection={"multiple"}
                                enableRangeSelection={false}
                                enableCellTextSelection={true}
                                isFirst={true}
                                displayNo={true}
                                searchApi={searchApi}
                                insertApi={insertApi}
                                deleteApi={deleteApi}
                                onClearApi={onClearApi}
                                insertRefresh={onSearch}
                                deleteRefresh={onSearch}
                                onCellValueChangedEvent={async () => {}}
                            />
                        </div>
                    </div>
                </div>
            </Content>
        </>
    );
}

export default WST01;
